import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { AUTH_API } from '@/config/api';
import { getValidRedirectPath } from '@/config/authRedirect';
import { finishAuthRedirect } from '@/config/hosts';

export default function OtpForm({ onSuccess, onUsePassword, role = 'patient' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [step, setStep] = useState('request');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [healthData, setHealthData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const finish = async (user) => {
    await login(user, null);
    if (onSuccess) {
      onSuccess(user);
      return;
    }
    const redirectParam = searchParams.get('redirect');
    const defaultDest = role === 'doctor' ? '/oncoagent/app' : '/app';
    const redirectPath = getValidRedirectPath(redirectParam);
    finishAuthRedirect(navigate, redirectPath || defaultDest);
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(
        `${AUTH_API}/otp/request`,
        { identifier: identifier.trim() },
        { withCredentials: true, timeout: 20000 }
      );
      setStep('verify');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setError('Please wait a moment before requesting another code.');
      else if (status === 400) setError('Enter a valid email or mobile number.');
      else if (status === 503) setError('Could not send a code right now. Try again shortly.');
      else setError('Could not send a code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(
        `${AUTH_API}/otp/verify`,
        {
          identifier: identifier.trim(),
          code: code.trim(),
          consent: { privacy, healthData },
        },
        { withCredentials: true, timeout: 20000 }
      );
      const user = response.data?.user;
      if (!user) throw new Error('Invalid response');
      await finish(user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const codeName = typeof detail === 'object' ? detail?.error : detail;
      if (codeName === 'consent_required' || err.response?.status === 400 && detail?.error === 'consent_required') {
        setError('Please accept the privacy notice and health-data processing to continue.');
      } else if (err.response?.status === 401) {
        setError('That code is incorrect. Try again.');
      } else if (err.response?.status === 400) {
        setError('That code has expired. Request a new one.');
        setStep('request');
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-[18px] bg-[#eef3ea] px-6 py-7 sm:px-7 sm:py-8 text-charcoal">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-forest-ink mb-1" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
          Sign in
        </h2>
        <p className="text-sm text-[#3d4a42]">
          A code to your email or phone. No password to remember at 3am.
        </p>
      </div>

      {error && (
        <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {step === 'request' ? (
        <form onSubmit={handleRequest} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
              Email or mobile
            </label>
            <input
              id="identifier"
              type="text"
              autoComplete="username"
              placeholder="you@example.com or 98XXXXXXXX"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-semibold bg-forest-ink text-cream-paper hover:bg-forest-shadow disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerify} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
              6-digit code
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 text-sm tracking-widest text-gray-900 focus:outline-none focus:ring-2 focus:ring-forest-ink"
              required
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1" />
            <span>I have read the privacy notice and agree to the processing of my data.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={healthData} onChange={(e) => setHealthData(e.target.checked)} className="mt-1" />
            <span>I consent to processing health information so Vani can answer my questions.</span>
          </label>
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-semibold bg-forest-ink text-cream-paper hover:bg-forest-shadow disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
          </button>
          <button
            type="button"
            className="w-full text-sm text-forest-ink underline"
            onClick={() => { setStep('request'); setCode(''); setError(''); }}
          >
            Use a different email or number
          </button>
        </form>
      )}

      {onUsePassword && (
        <button
          type="button"
          onClick={onUsePassword}
          className="mt-6 w-full text-sm text-gray-600 underline"
        >
          Use email and password instead
        </button>
      )}
    </div>
  );
}
